import { useCallback, useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { AuthContext } from "../../context/auth-context";
import { ClinicaContext } from "../../context/clinica-context";
import { useWebSocketSubscription } from "../../context/websocket-context";
import { useHttpClient } from "../../hooks/http-hook";
import UrgenciasModal from "../../../pages/home/medicoAdministrador/components/UrgenciasModal";
import ModalInfo from "../UIElements/ModalInfo";
import ModalDispensarUrgencia from "../urgencia/ModalDispensarUrgencia";
import "./BarraUrgencias.scss";

/**
 * A faixa que diz, em qualquer ecrã, que há alguém na sala de espera.
 *
 * ─── PORQUE EXISTE ──────────────────────────────────────────────────────────
 *
 * O aviso de urgência era um número a ficar carmesim num cartão da dashboard.
 * Um dentista a trabalhar no odontograma não via nada; recarregar a página fora
 * da dashboard também não mostrava nada.
 *
 * Isto vive acima das rotas, por isso está presente em todos os ecrãs, e
 * desaparece sozinha quando a fila fica vazia.
 *
 * ─── PORQUE NÃO É UM MODAL QUE SALTA ────────────────────────────────────────
 *
 * Um modal atirado por cima de quem está a meio de escrever uma evolução
 * ensina a fechá-lo sem ler, e perde-se o ecrã e o aviso. Uma faixa fixa
 * interrompe sem tapar: está sempre lá até alguém atender.
 */

/* A chave do interruptor do som. Por utilizador e por navegador: quem está ao
   balcão com o som de uma clínica cheia quer poder desligá-lo sem o desligar
   para os outros. */
const CHAVE_SOM = "odonto.urgencia.som";

/* Quem pode dizer «Vou atender agora». Igual a `PAPEIS_CLINICOS` em
   `helpers/avisoUrgencia.mjs`, que é quem o servidor aceita — se divergirem, o
   ecrã mostra um botão que dá 403. */
const PAPEIS_QUE_ASSUMEM = ["Dentista", "Médico/Administrador"];

const TITULO_BASE = document.title;

function BarraUrgencias() {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();

  const [urgencias, setUrgencias] = useState([]);
  const [abrirLista, setAbrirLista] = useState(false);
  const [comSom, setComSom] = useState(() => {
    /* Pode atirar em janela privada ou com os dados do site bloqueados. */
    try {
      return localStorage.getItem(CHAVE_SOM) !== "off";
    } catch {
      return true;
    }
  });
  const [modalProps, setModalProps] = useState({ show: false });
  /* `{ id, nome }` da urgência a dispensar, ou `null`. O motivo é pedido num
     modal próprio — o `ModalInfo` só tem sim/não e não recolhe nada. */
  const [aDispensar, setADispensar] = useState(null);
  const [dispensando, setDispensando] = useState(false);

  const clinicaId = clinica?.clinica?._id;

  /* O `tocar` mais recente, para o subscritor do websocket o chamar sem se
     voltar a inscrever de cada vez que o interruptor do som muda. */
  const tocarRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!clinicaId || !auth.token) return;
    try {
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setUrgencias(res.urgencias || []);
    } catch (err) {
      /* Em silêncio: uma faixa de aviso que começa a dar erros é pior do que
         não existir. A lista volta a ser pedida no próximo evento. */
    }
  }, [clinicaId, auth.token, sendRequest]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /* ── O som ───────────────────────────────────────────────────────────────
   *
   * Gerado com a WebAudio em vez de um ficheiro: dois tons curtos, sem pedir
   * um .mp3 ao servidor nem engordar o bundle.
   *
   * O navegador BLOQUEIA áudio até a pessoa ter interagido com a página. É por
   * isso que isto falha em silêncio no primeiro carregamento e passa a
   * funcionar depois do primeiro clique — e é também por isso que o som nunca
   * pode ser o único aviso. */
  const tocar = useCallback(() => {
    if (!comSom) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const agora = ctx.currentTime;

      [0, 0.22].forEach((atraso, i) => {
        const osc = ctx.createOscillator();
        const vol = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = i === 0 ? 880 : 660;
        vol.gain.setValueAtTime(0.0001, agora + atraso);
        vol.gain.exponentialRampToValueAtTime(0.25, agora + atraso + 0.02);
        vol.gain.exponentialRampToValueAtTime(0.0001, agora + atraso + 0.18);
        osc.connect(vol).connect(ctx.destination);
        osc.start(agora + atraso);
        osc.stop(agora + atraso + 0.2);
      });

      setTimeout(() => ctx.close?.(), 1000);
    } catch {
      /* Sem som é feio, não é grave. */
    }
  }, [comSom]);

  useEffect(() => {
    tocarRef.current = tocar;
  }, [tocar]);

  /* ── O título do separador ───────────────────────────────────────────────
   *
   * É a única coisa que chega a quem mudou para o WhatsApp. Reposto ao
   * desmontar, senão o título fica preso com um contador antigo. */
  useEffect(() => {
    document.title = urgencias.length
      ? `(${urgencias.length}) ⚠ Urgência — ${TITULO_BASE}`
      : TITULO_BASE;
    return () => {
      document.title = TITULO_BASE;
    };
  }, [urgencias.length]);

  /* ── Empurrar a aplicação para baixo ────────────────────────────────────
   *
   * A faixa é `fixed` e taparia os primeiros 33px de qualquer ecrã. A classe no
   * `body` é posta aqui, em JavaScript, e não com um `body:has(.barraUrgencias)`
   * no SCSS: o `:has()` não existe em navegadores mais antigos e a falha dele é
   * silenciosa — o topo da página ficava tapado sem nada a explicar porquê. */
  const haUrgencias = urgencias.length > 0;
  useEffect(() => {
    const classe = "com-barra-urgencias";
    document.body.classList.toggle(classe, haUrgencias);
    return () => document.body.classList.remove(classe);
  }, [haUrgencias]);

  useWebSocketSubscription(
    async (dados) => {
      /* O servidor já decide quem recebe cada evento: filtra por clínica e por
         unidade. Aqui basta voltar a pedir a lista, que é a fonte da verdade —
         e assim um «vou atender» feito por outra pessoa também apaga a faixa. */
      if (dados?.tabela === "urgencias") {
        carregar();
        return;
      }

      /* ── O som segue a NOTIFICAÇÃO, não o contador ──────────────────────────
       *
       * Estava preso ao número de urgências subir, e por isso a escalada dos 15
       * minutos chegava ao sino em silêncio — quando o ponto da escalada é
       * precisamente voltar a chamar a atenção de quem não reagiu.
       *
       * Prendê-lo à notificação resolve isso e acerta noutra coisa: só toca a
       * quem a notificação foi dirigida. Um dentista que o balcão não escolheu
       * continua a ver a faixa, mas não é interrompido por um som. */
      if (dados?.tabela === "notificacaos" && dados.tipoDeOperacao === "insert") {
        const n = dados.documento;
        const deUrgencia =
          n?.tipo === "urgencia" || n?.tipo === "urgencia-escalada";
        const paraMim = n?.user?.some?.(
          (u) => String(u) === String(auth.userId),
        );
        if (deUrgencia && paraMim) tocarRef.current?.();
      }
    },
    [carregar, auth.userId],
    "BarraUrgencias",
  );

  const fecharModalInfo = () =>
    setModalProps((atual) => ({ ...atual, show: false }));

  const marcarAtendido = async (id) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias/atendido/${id}`,
        "PATCH",
        {},
        { Authorization: "Bearer " + auth.token },
      );
      setUrgencias((atual) => atual.filter((u) => (u.id || u._id) !== id));
      toast.success("Urgência sua. A equipa deixou de ser avisada.");
    } catch (err) {
      /* A mensagem do servidor, para o 403 do `exigirClinico` dizer «só um
         dentista pode assumir uma urgência» em vez de um erro genérico. */
      toast.error(err.message || "Não foi possível assumir a urgência.");
    } finally {
      fecharModalInfo();
    }
  };

  const dispensar = async ({ motivoDispensa, notaDispensa }) => {
    if (!aDispensar) return;
    setDispensando(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias/dispensar/${aDispensar.id}`,
        "PATCH",
        JSON.stringify({ motivoDispensa, notaDispensa }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      await carregar();
      toast.success("Urgência dispensada.");
      setADispensar(null);
    } catch (err) {
      toast.error(err.message || "Não foi possível dispensar a urgência.");
    } finally {
      setDispensando(false);
    }
  };

  const confirmarDispensar = (id, nome) => {
    setAbrirLista(false);
    setADispensar({ id, nome });
  };

  const confirmarAtendido = (id, nome) => {
    setAbrirLista(false);
    setModalProps({
      show: true,
      icon: "info",
      title: "Urgência",
      message: `Confirma que vai atender ${nome} agora? A urgência sai da fila e deixa de avisar a equipa.`,
      form: true,
      onCancel: fecharModalInfo,
      onConfirm: () => marcarAtendido(id),
    });
  };

  const alternarSom = () => {
    const novo = !comSom;
    setComSom(novo);
    try {
      localStorage.setItem(CHAVE_SOM, novo ? "on" : "off");
    } catch {
      /* Sem memória do interruptor; vale para esta sessão. */
    }
  };

  if (!auth.isLoggedIn || !urgencias.length) return null;

  const primeira = urgencias[0];

  return (
    <>
      <div className="barraUrgencias" role="status" aria-live="polite">
        <span className="barraUrgencias__ponto" aria-hidden="true" />

        <span className="barraUrgencias__texto">
          {urgencias.length === 1
            ? `${primeira.cliente?.nome || "Um paciente"} está na sala de espera`
            : `${urgencias.length} pacientes de urgência à espera`}
          {urgencias.length === 1 && primeira.motivo && ` — ${primeira.motivo}`}
        </span>

        <button
          type="button"
          className="barraUrgencias__btn"
          onClick={() => setAbrirLista(true)}
        >
          Ver
        </button>

        <button
          type="button"
          className="barraUrgencias__som"
          onClick={alternarSom}
          title={comSom ? "Desligar o som dos avisos" : "Ligar o som dos avisos"}
          aria-label={comSom ? "Desligar o som" : "Ligar o som"}
        >
          {comSom ? "🔔" : "🔕"}
        </button>
      </div>

      <UrgenciasModal
        urgencias={urgencias}
        openModalUrgencia={abrirLista}
        handleClose={() => setAbrirLista(false)}
        atendidoConfirmation={confirmarAtendido}
        /* Dispensar nao e exclusivo de dentistas: tirar da fila quem foi embora
           e o mesmo gesto que o «Cancelar Urgencia» da ficha do cliente, que a
           secretaria sempre pode fazer. */
        aoDispensar={confirmarDispensar}
        /* A faixa aparece a todos, e foi por aqui que a secretária passou a
           poder assumir uma urgência — o que não pode. Ela vê a fila, que é o
           que precisa para atender o telefone, mas o botão é de quem atende. */
        podeAssumir={PAPEIS_QUE_ASSUMEM.includes(auth.role)}
      />

      <ModalDispensarUrgencia
        aberto={!!aDispensar}
        fechar={() => setADispensar(null)}
        nomeCliente={aDispensar?.nome}
        aoConfirmar={dispensar}
        aEnviar={dispensando}
      />

      {modalProps.show && <ModalInfo {...modalProps} />}
    </>
  );
}

export default BarraUrgencias;
